import { ApiConfig } from '../../../api/apiConfig/apiConfig';
import { DISABLE_ALL_NETWORK } from '../../../networkConfig';
import { parseFuturesSymbol } from './helpersFutures';
const { io } = require('socket.io-client');

/**
 * Live streaming for the standalone mobile futures chart (WebView).
 *
 * Uses the real futures feed (same as the desktop futures page / SocketContext):
 *   emit  futures:subscribe { symbol: "BTCUSDT-PERP" }   → continuous updates
 *   recv  futures:update {
 *           type: "contracts_only", contracts: [ { base_asset, quote_asset, mark_price, ... } ]   // snapshot
 *           type: "contract_data",  contract:  { base_asset, quote_asset, mark_price, ... }        // continuous
 *         }
 *   recv  futures:price { symbol, mark_price, last_price, ... }                                     // sub-second tick
 *
 * Chart symbols arrive as "BTCUSDT_PERP"; contract symbols use a hyphen ("BTCUSDT-PERP").
 */

const channelToSubscription = new Map(); // key: symbolInfo.name e.g. "BTCUSDT_PERP"

let socket = null;
let isSocketInitialized = false;
let futuresUpdateHandler = null;
let futuresPriceHandler = null;
let pendingStreamParams = null;

function getStartOfMinute(timestamp) {
  const date = new Date(timestamp);
  date.setSeconds(0, 0);
  return date.getTime();
}

// "BTCUSDT_PERP" -> "BTCUSDT-PERP" (server's contract symbol format)
function toContractSymbol(name) {
  return String(name || '').toUpperCase().replace(/_/g, '-');
}

// normalise any symbol ("BTCUSDT-PERP", "BTCUSDT_PERP", "BTCUSDT") -> "BTCUSDT"
function normaliseSymbol(sym) {
  return String(sym || '').toUpperCase().replace(/[-_]/g, '').replace(/PERP$/, '');
}

function contractPrice(c) {
  const p = parseFloat(c?.mark_price ?? c?.last_price ?? c?.bid_price ?? c?.ask_price);
  return Number.isFinite(p) ? p : null;
}

// contract.updated_at / payload.timestamp are epoch milliseconds
function contractTimeMs(c, fallbackTs) {
  const t = Number(c?.updated_at ?? fallbackTs);
  return Number.isFinite(t) && t > 0 ? t : Date.now();
}

function contractMatches(c, parsed) {
  const base = String(c?.base_asset ?? c?.short_name ?? c?.base_currency ?? '').toUpperCase();
  const quote = String(c?.quote_asset ?? c?.margin_asset ?? c?.quote_currency ?? '').toUpperCase();
  if (base && quote) return base === parsed.base && quote === parsed.quote;
  return normaliseSymbol(c?.symbol) === parsed.raw;
}

function applyTick(subscriptionItem, price, timeMs) {
  if (!Number.isFinite(price)) return;

  if (!subscriptionItem.lastDailyBar) {
    subscriptionItem.lastDailyBar = {
      time: timeMs,
      open: price,
      high: price,
      low: price,
      close: price,
      volume: 0,
    };
  } else {
    const lastBarMinute = getStartOfMinute(subscriptionItem.lastDailyBar.time);
    const currentMinute = getStartOfMinute(timeMs);
    if (currentMinute > lastBarMinute) {
      subscriptionItem.lastDailyBar = {
        time: currentMinute,
        open: subscriptionItem.lastDailyBar.close,
        high: price,
        low: price,
        close: price,
        volume: 0,
      };
    } else {
      subscriptionItem.lastDailyBar = {
        ...subscriptionItem.lastDailyBar,
        high: Math.max(subscriptionItem.lastDailyBar.high ?? price, price),
        low: Math.min(subscriptionItem.lastDailyBar.low ?? price, price),
        close: price,
      };
    }
  }

  subscriptionItem.handlers.forEach((h) => h.callback(subscriptionItem.lastDailyBar));
}

function handleContract(c, parsed, subscriptionItem, fallbackTs) {
  if (!contractMatches(c, parsed)) return;
  const price = contractPrice(c);
  if (price == null) return;
  applyTick(subscriptionItem, price, contractTimeMs(c, fallbackTs));
}

function setupListeners() {
  if (!socket) return;

  if (futuresUpdateHandler) socket.off('futures:update', futuresUpdateHandler);
  if (futuresPriceHandler) socket.off('futures:price', futuresPriceHandler);

  // futures:update — contracts_only (snapshot array) and contract_data (single contract)
  futuresUpdateHandler = (data) => {
    try {
      if (!data) return;
      const fallbackTs = data?.timestamp;
      const contracts = Array.isArray(data?.contracts) ? data.contracts : null;
      const single = data?.contract || null;

      for (const [channelString, subscriptionItem] of channelToSubscription) {
        const parsed = parseFuturesSymbol(channelString);
        if (!parsed?.base || !parsed?.quote) continue;

        if (single) {
          handleContract(single, parsed, subscriptionItem, fallbackTs);
        } else if (contracts) {
          const c = contracts.find((x) => contractMatches(x, parsed));
          if (c) handleContract(c, parsed, subscriptionItem, fallbackTs);
        }
      }
    } catch (err) {
      // Silent — never crash the chart
    }
  };

  // futures:price — sub-second tick { symbol, mark_price, last_price }
  futuresPriceHandler = (data) => {
    try {
      if (!data) return;
      const price = parseFloat(data?.mark_price ?? data?.last_price ?? data?.index_price);
      if (!Number.isFinite(price)) return;

      const symNorm = normaliseSymbol(data?.symbol);
      const timeMs = contractTimeMs(data, data?.timestamp);

      for (const [channelString, subscriptionItem] of channelToSubscription) {
        const parsed = parseFuturesSymbol(channelString);
        if (symNorm && parsed?.raw && symNorm !== parsed.raw) continue;
        applyTick(subscriptionItem, price, timeMs);
      }
    } catch (err) {
      // Silent
    }
  };

  socket.on('futures:update', futuresUpdateHandler);
  socket.on('futures:price', futuresPriceHandler);
}

function emitSubscribeForAll() {
  if (!socket?.connected) return;
  for (const channelString of channelToSubscription.keys()) {
    socket.emit('futures:subscribe', { symbol: toContractSymbol(channelString) });
  }
}

function addSubscription(params) {
  const { symbolInfo, resolution, onRealtimeCallback, subscriberUID, lastDailyBar } = params;
  const channelString = symbolInfo.name;
  const handler = { id: subscriberUID, callback: onRealtimeCallback };

  let subscriptionItem = channelToSubscription.get(channelString);
  if (subscriptionItem) {
    subscriptionItem.handlers.push(handler);
    if (lastDailyBar) subscriptionItem.lastDailyBar = lastDailyBar;
  } else {
    subscriptionItem = {
      subscriberUID,
      resolution,
      lastDailyBar: lastDailyBar ?? null,
      handlers: [handler],
    };
    channelToSubscription.set(channelString, subscriptionItem);
  }

  setupListeners();
  return channelString;
}

const initializeSocket = () => {
  if (DISABLE_ALL_NETWORK) {
    return;
  }
  if (socket && isSocketInitialized) {
    return;
  }

  socket = io(ApiConfig?.webSocketUrl, {
    transports: ['websocket'],
    upgrade: false,
    rejectUnauthorized: false,
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 2000,
  });

  isSocketInitialized = true;

  socket.on('connect', () => {
    setupListeners();

    if (pendingStreamParams) {
      addSubscription(pendingStreamParams);
      pendingStreamParams = null;
    }

    // (Re)subscribe every active channel — also covers reconnects.
    emitSubscribeForAll();
  });

  socket.on('disconnect', () => {});
};

export async function subscribeFuturesOnStream(
  symbolInfo,
  resolution,
  onRealtimeCallback,
  subscriberUID,
  onResetCacheNeededCallback,
  lastDailyBar
) {
  initializeSocket();

  const params = {
    symbolInfo,
    resolution,
    onRealtimeCallback,
    subscriberUID,
    lastDailyBar,
  };

  if (socket?.connected) {
    const channelString = addSubscription(params);
    socket.emit('futures:subscribe', { symbol: toContractSymbol(channelString) });
  } else {
    pendingStreamParams = params;
  }
}

export function unsubscribeFuturesFromStream(subscriberUID) {
  for (const [channelString, subscriptionItem] of channelToSubscription) {
    const handlerIndex = subscriptionItem.handlers.findIndex((h) => h.id === subscriberUID);

    if (handlerIndex !== -1) {
      subscriptionItem.handlers.splice(handlerIndex, 1);

      if (subscriptionItem.handlers?.length === 0) {
        channelToSubscription.delete(channelString);
        if (socket?.connected) {
          socket.emit('futures:unsubscribe');
        }
      }
      break;
    }
  }
}

export function disconnectMobileFuturesChartSocket() {
  if (socket) {
    if (socket.connected) {
      socket.emit('futures:unsubscribe');
    }
    if (futuresUpdateHandler) {
      socket.off('futures:update', futuresUpdateHandler);
      futuresUpdateHandler = null;
    }
    if (futuresPriceHandler) {
      socket.off('futures:price', futuresPriceHandler);
      futuresPriceHandler = null;
    }
    socket.off('connect');
    socket.off('disconnect');
    socket.disconnect();
    socket = null;
    isSocketInitialized = false;
  }
  pendingStreamParams = null;
  channelToSubscription.clear();
}
